import React from 'react';
import PropTypes from 'prop-types';

/**
 * FormFilters - Filter controls for forms management
 *
 * Provides:
 * - Form type dropdown
 * - User filter dropdown (Admin/Office only)
 * - Page size selector
 * - Sort controls
 *
 * @param {Object} props
 */
const FormFilters = ({
  // Form type
  selectedFormType,
  onFormTypeChange,
  formTypes,
  formCounts,
  // User filter
  selectedUser,
  onUserChange,
  portalUserList,
  showUserFilter,
  // Pagination
  pageSize,
  onPageSizeChange,
  // Sorting
  sortBy,
  sortDirection,
  onSortChange,
  // State
  isDM,
}) => {
  return (
    <>
      {/* Page Controls */}
      <div className="section-card" style={{ marginBottom: '24px' }}>
        <h2>Page Controls</h2>
        <div
          style={{
            display: 'flex',
            gap: '20px',
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <div
            className="form-group"
            style={{ marginBottom: 0, minWidth: '150px' }}
          >
            <label htmlFor="pageSize">Items per page:</label>
            <select
              id="pageSize"
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          <div
            className="form-group"
            style={{ marginBottom: 0, minWidth: '150px' }}
          >
            <label htmlFor="sortBy">Sort by:</label>
            <select
              id="sortBy"
              value={sortBy}
              onChange={(e) => onSortChange(e.target.value, sortDirection)}
            >
              <option value="id">ID</option>
              <option value="submittedDate">Submission Date</option>
            </select>
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Direction:</label>
            <button
              className="btn-secondary"
              onClick={() =>
                onSortChange(sortBy, sortDirection === 'asc' ? 'desc' : 'asc')
              }
              style={{ marginTop: '4px' }}
            >
              {sortDirection === 'asc' ? '↑ Ascending' : '↓ Descending'}
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="section-card">
        <h2>Filters</h2>
        {isDM && (
          <p style={{ color: '#718096', marginBottom: '16px' }}>
            Showing only your submitted forms
          </p>
        )}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isDM ? '1fr' : '1fr 1fr',
            gap: '20px',
          }}
        >
          <div className="form-group">
            <label htmlFor="formTypeFilter">Form Type</label>
            <select
              id="formTypeFilter"
              value={selectedFormType}
              onChange={(e) => onFormTypeChange(e.target.value)}
            >
              {formTypes.map((formType) => (
                <option key={formType} value={formType}>
                  {formType.charAt(0).toUpperCase() + formType.slice(1)} (
                  {formCounts[formType] ?? 0})
                </option>
              ))}
            </select>
          </div>

          {showUserFilter && (
            <div className="form-group">
              <label htmlFor="userFilter">Filter by User</label>
              <select
                id="userFilter"
                value={selectedUser}
                onChange={(e) => onUserChange(e.target.value)}
                className={selectedUser !== 'all' ? 'select-active' : ''}
              >
                <option value="all">All Users</option>
                {portalUserList
                  ?.slice()
                  ?.sort((a, b) => {
                    const aName = `${a?.userFirst || ''} ${a?.userLast || ''} ${
                      a?.userName || ''
                    }`
                      .trim()
                      .toLowerCase();
                    const bName = `${b?.userFirst || ''} ${b?.userLast || ''} ${
                      b?.userName || ''
                    }`
                      .trim()
                      .toLowerCase();
                    if (aName < bName) return -1;
                    if (aName > bName) return 1;
                    return 0;
                  })
                  .map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.userFirst} {user.userLast} ({user.userName})
                    </option>
                  ))}
              </select>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

FormFilters.propTypes = {
  // Form type
  selectedFormType: PropTypes.string.isRequired,
  onFormTypeChange: PropTypes.func.isRequired,
  formTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
  formCounts: PropTypes.object,
  // User filter
  selectedUser: PropTypes.string,
  onUserChange: PropTypes.func,
  portalUserList: PropTypes.array,
  showUserFilter: PropTypes.bool,
  // Pagination
  pageSize: PropTypes.number.isRequired,
  onPageSizeChange: PropTypes.func.isRequired,
  // Sorting
  sortBy: PropTypes.string.isRequired,
  sortDirection: PropTypes.oneOf(['asc', 'desc']).isRequired,
  onSortChange: PropTypes.func.isRequired,
  // State
  isDM: PropTypes.bool,
};

FormFilters.defaultProps = {
  formCounts: {},
  selectedUser: 'all',
  portalUserList: [],
  showUserFilter: false,
  isDM: false,
};

export default FormFilters;
